import { useEffect, useState } from "react";

import { Radio, Spinner, VisualPicker } from "@salesforce/design-system-react";

import { Button } from "@vimeo/iris/components";

import {
    AnimatedThumbnail,
    ListResponse,
    ThumbnailType,
    VimeoAsset,
} from "vimeo";
import { openCreateGifWidget, appOrigin } from "../../common";

import {
    client,
    ERROR_CODE_VIDEO_NOT_FOUND_USER_DOES_NOT_OWN_VIDEO,
} from "../../vimeoClient";
import { EventName, sendAnalyticsEvent } from "../../analytics/common";

interface Props {
    videoUri: string;
    onSelect: (
        type: ThumbnailType,
        imgUrl: string,
        imgUrlWithPlayButton: string,
        isPortrait: boolean
    ) => void;
}

export function Gifs(props: Props) {
    const [gifs, setGifs] = useState<AnimatedThumbnail[]>([]);
    const [loading, setLoading] = useState(false);
    const [videoId, setVideoId] = useState<string>();
    const [showAccessError, setShowAccessError] = useState(false);

    async function getAnimatedThumbnails() {
        const resp = await client.get<ListResponse<VimeoAsset>>(
            `/api/vimeo${props.videoUri}/assets`
        );
        setGifs(
            resp.data.data
                .filter((asset) => asset.type === "gif")
                .map((a) => a.item as AnimatedThumbnail)
        );
    }

    useEffect(() => {
        if (!props.videoUri) {
            return;
        }

        setVideoId(props.videoUri.replace("/videos/", ""));
        setLoading(true);

        getAnimatedThumbnails()
            .catch((e) => {
                if (
                    e.response?.data?.error_code ===
                    ERROR_CODE_VIDEO_NOT_FOUND_USER_DOES_NOT_OWN_VIDEO
                ) {
                    setShowAccessError(true);
                }
                console.error(e);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.videoUri]);

    function onChange(event: any) {
        const index = event.target.value;
        const gif = gifs[index];
        // The item in the second index position seems to always
        // have the link_with_play_button populated. So let's
        // use that. This is especially the case with GIFs.
        props.onSelect(
            "gif",
            gif.sizes[1].link,
            gif.sizes[1].link_with_play_button,
            gif.sizes[1].height > gif.sizes[1].width
        );
    }

    const gifEls = gifs?.map((t, index) => (
        <Radio
            key={index}
            value={`${index}`}
            onRenderVisualPicker={() => (
                <span>
                    <img
                        style={{
                            maxWidth:
                                t.sizes[1].height > t.sizes[1].width
                                    ? "40%"
                                    : "100%",
                        }}
                        src={t.sizes[1].link}
                        alt=""
                    />
                </span>
            )}
            onChange={onChange}
        />
    ));

    async function onVisibilityChange() {
        if (document.visibilityState !== "visible") {
            return;
        }

        window.document.removeEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        setLoading(true);
        await getAnimatedThumbnails();
        setLoading(false);
    }

    async function onMessage(e: MessageEvent) {
        if (e.origin !== appOrigin) {
            return;
        }

        setLoading(true);
        await getAnimatedThumbnails();
        setLoading(false);
    }

    async function onCreateGifClicked() {
        sendAnalyticsEvent(EventName.integrationsClick, {
            name: "open_gif_modal",
            location: "embed_in_email",
            video_id: videoId,
            cta: "create_gif",
        });

        const opened = await openCreateGifWidget(videoId);

        window.document.addEventListener(
            "visibilitychange",
            onVisibilityChange
        );

        if (!opened) {
            return;
        }

        opened.onmessage = onMessage;
    }

    if (showAccessError) {
        return (
            <div className="slds-var-m-top_large">
                Unable to embed in email <br />
                Viewer cannot Embed in Email. <br />
                Contact your admin to change your permissions.
            </div>
        );
    }

    return (
        <div>
            {loading && <Spinner />}
            <div style={{ display: "flex", justifyContent: "end" }}>
                <Button format="secondary" onClick={onCreateGifClicked}>
                    Create GIF
                </Button>
            </div>

            {gifEls && gifEls.length > 0 && (
                <VisualPicker className="gifs-visual-picker">
                    {gifEls}
                </VisualPicker>
            )}
            {!loading && (!gifEls || !gifEls.length) && (
                <div className="slds-var-m-top_large">
                    You have no GIFs created.
                </div>
            )}
        </div>
    );
}
